import React, { useState, type FormEvent } from 'react';
import { Button } from '../shared';

export interface LockScreenProps {
  onUnlock: (password: string) => Promise<boolean>;
}

/**
 * Unlock form shown when the lock is configured and the app is locked.
 * Reports an "Incorrect password" error and clears the field on failure.
 */
export function LockScreen({ onUnlock }: LockScreenProps): React.ReactElement {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    const ok = await onUnlock(password);
    if (!ok) {
      setError('Incorrect password.');
      setPassword('');
      setBusy(false);
    }
    // On success the AppGate swaps to <App />; this component unmounts.
  };

  return (
    <div className="applock-screen" role="main">
      <form className="applock-card" onSubmit={handleSubmit}>
        <div className="applock-card__icon" aria-hidden="true">
          &#128274;
        </div>
        <h1 className="applock-card__title">App Locked</h1>
        <p className="applock-card__text">Enter your password to continue.</p>

        <div className="input-group">
          <label htmlFor="applock-unlock-password" className="input-group__label">
            Password
          </label>
          <input
            id="applock-unlock-password"
            className="input-group__input"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={!!error}
          />
        </div>

        {error && (
          <p className="applock-card__error" role="alert">
            {error}
          </p>
        )}

        <div className="applock-card__actions">
          <Button variant="primary" size="md" type="submit" loading={busy}>
            Unlock
          </Button>
        </div>
      </form>
    </div>
  );
}
